"use client";

import ContentContainer from "@/components/ContentContainer/ContentContainer";
import { MODAL_CONTENT } from "@/constants/modalContent";
import useElementViewed from "@/hooks/tagular/use-element-viewed";
import cn from "@/lib/cn";
import type { TabData } from "@/types/tabsBento";
import dynamic from "next/dynamic";
import React, { useState } from "react";
import BentoHeader from "../BentoHeader/BentoHeader";
import BentoTab from "../BentoTab/BentoTab";

const Modal = dynamic(() => import("@/components/Modal"), { ssr: false });

export interface SingleBentoContainerProps {
  tabData: TabData;
  sectionTitle?: string;
  sectionSubtitle?: string;
  containerClassName?: string;
  onButtonClick?: (action: string) => void;
}

const SingleBentoContainer: React.FC<SingleBentoContainerProps> = ({
  tabData,
  sectionTitle = "Get more with Xfinity",
  sectionSubtitle,
  containerClassName = "max-w-full md:max-w-5xl lg:max-w-384 !px-10 md:!px-8 lg:!px-16",
  onButtonClick,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalContent, setActiveModalContent] = useState<string | null>(
    null
  );

  const handleButtonClick = (action: string) => {
    // Check if this action corresponds to modal content
    if (MODAL_CONTENT[action]) {
      setActiveModalContent(action);
      setIsModalOpen(true);
    } else if (onButtonClick) {
      onButtonClick(action);
    } else {
      console.log(`Non-modal action: ${action}`);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveModalContent(null);
  };

  const modalData = activeModalContent
    ? MODAL_CONTENT[activeModalContent]
    : null;

  useElementViewed(ref, {
    location: "bento",
    name: "bentoSingleContainer",
    text: sectionTitle,
  });

  return (
    <>
      <ContentContainer className={cn("bg-neutral-light", containerClassName)}>
        <BentoHeader title={sectionTitle} subtitle={sectionSubtitle} />

        <div className="tab-content" ref={ref}>
          <BentoTab
            id={tabData.id}
            cards={tabData.cards}
            gridClass={tabData.gridClass}
            layoutType={tabData.layoutType}
            columns={tabData.columns}
            footer={tabData.footer}
            onButtonClick={handleButtonClick}
            tabType={tabData.id}
            sliderClassName={tabData.sliderClassName}
            sliderOverflowStyle={tabData.sliderOverflowStyle}
          />
        </div>
      </ContentContainer>

      {modalData ? (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={modalData.title}
          actions={modalData.actions}
          tagularProps={{
            name: `modal${modalData.elementName}`,
          }}
        >
          {modalData.content}
        </Modal>
      ) : null}
    </>
  );
};

export default SingleBentoContainer;
